import type { WorldAlignment } from '../contracts';
import { type QuatLike, RAD2DEG, type Vec3, clamp, wrap180, wrap360 } from './math';

/**
 * Heading of the XR viewer forward (-Z) in the XR 'local' space:
 * thetaXr = atan2(f.x, -f.z) in degrees [0, 360) (contracts.ts).
 */
export function thetaXrDeg(q: QuatLike): number {
  const x = q[0], y = q[1], z = q[2], w = q[3];
  const fx = -2 * (x * z + w * y);
  const fz = -(1 - 2 * (x * x + y * y));
  return wrap360(Math.atan2(fx, -fz) * RAD2DEG);
}

/** Elevation of the viewer forward above the horizontal plane (deg). */
export function xrPitchDeg(q: QuatLike): number {
  const x = q[0], y = q[1], z = q[2], w = q[3];
  const fy = -2 * (y * z - w * x);
  return Math.asin(clamp(fy, -1, 1)) * RAD2DEG;
}

/**
 * Alignment M = T(tx, waterY, tz) * R_y(yawRad) (XR <- WorldENU) such that the
 * WorldENU camera position (e, h, -n) maps horizontally onto cXr:
 * t = cXr - R_y(yawRad) * (e, h, -n); the vertical part is the filtered waterY.
 */
export function computeAlignment(
  yawRad: number,
  cXr: ArrayLike<number>,
  e: number,
  n: number,
  waterY: number,
  out: WorldAlignment,
): WorldAlignment {
  const c = Math.cos(yawRad);
  const s = Math.sin(yawRad);
  const px = e;
  const pz = -n;
  out.yawRad = yawRad;
  out.tx = cXr[0] - (c * px + s * pz);
  out.tz = cXr[2] - (-s * px + c * pz);
  out.waterY = waterY;
  return out;
}

/** out = M * (x, y, z) for WorldENU point (x, y, z). */
export function applyAlignment(a: WorldAlignment, x: number, y: number, z: number, out: Vec3): Vec3 {
  const c = Math.cos(a.yawRad);
  const s = Math.sin(a.yawRad);
  out[0] = c * x + s * z + a.tx;
  out[1] = y + a.waterY;
  out[2] = -s * x + c * z + a.tz;
  return out;
}

/**
 * Short ring buffer of XR viewer headings so a (slightly delayed) orientation
 * sensor sample can be compared with the XR heading at the sample's own time.
 */
export class HeadingHistory {
  private readonly times: Float64Array;
  private readonly headings: Float64Array;
  private count = 0;
  private head = 0;

  constructor(size = 32) {
    this.times = new Float64Array(size);
    this.headings = new Float64Array(size);
  }

  clear(): void {
    this.count = 0;
    this.head = 0;
  }

  get size(): number {
    return this.count;
  }

  push(time: number, headingDeg: number): void {
    if (this.count > 0) {
      const last = (this.head + this.times.length - 1) % this.times.length;
      if (time <= this.times[last]) {
        this.headings[last] = headingDeg;
        this.times[last] = time;
        return;
      }
    }
    this.times[this.head] = time;
    this.headings[this.head] = headingDeg;
    this.head = (this.head + 1) % this.times.length;
    if (this.count < this.times.length) this.count++;
  }

  /**
   * Heading at `time` (linear interpolation across the wrap), clamped to the
   * oldest/newest entry. NaN if empty or if `time` is older than the buffer
   * by more than `maxGapMs`.
   */
  at(time: number, maxGapMs = 250): number {
    const n = this.count;
    if (n === 0) return NaN;
    const len = this.times.length;
    const newest = (this.head + len - 1) % len;
    if (time >= this.times[newest]) return this.headings[newest];
    for (let k = 1; k < n; k++) {
      const i = (newest - k + len) % len;
      const t0 = this.times[i];
      if (time >= t0) {
        const j = (i + 1) % len;
        const t1 = this.times[j];
        const f = t1 > t0 ? (time - t0) / (t1 - t0) : 1;
        return wrap360(this.headings[i] + f * wrap180(this.headings[j] - this.headings[i]));
      }
    }
    const oldest = (newest - (n - 1) + len) % len;
    return this.times[oldest] - time <= maxGapMs ? this.headings[oldest] : NaN;
  }

  /** Absolute heading rate (deg/s) around `time` from neighbouring entries, 0 if unknown. */
  rateAt(time: number): number {
    const n = this.count;
    if (n < 2) return 0;
    const len = this.times.length;
    const newest = (this.head + len - 1) % len;
    let j = newest;
    for (let k = 1; k < n - 1; k++) {
      const i = (newest - k + len) % len;
      if (this.times[i] <= time) break;
      j = i;
    }
    const i = (j - 1 + len) % len;
    const dt = this.times[j] - this.times[i];
    if (dt <= 0) return 0;
    return (Math.abs(wrap180(this.headings[j] - this.headings[i])) * 1000) / dt;
  }
}
